import bookigFunnelSingleCompanyConfig from "@/state/bookigFunnelSingleCompanyConfig"
import { computed, ComputedRef } from "vue"

interface IUseBookingFunnelCSEConfig {
    isHideCreditCardSection: ComputedRef<boolean>,
    isHideInsuranceSection: ComputedRef<boolean>,
    successfulPaymentTitle: ComputedRef<string>,
    successfulPaymentText: ComputedRef<string>,
    successfulQuoteTitle: ComputedRef<string>,
    successfulQuoteText: ComputedRef<string>,
    isMandatoryParticipants: ComputedRef<boolean>,
    isMandatoryInsurance: ComputedRef<boolean>,
    isSkiSchoolNL: ComputedRef<boolean>
}

/**
 * A composable function to access the configuration for the booking funnel of a single company.
 *
 * This function returns computed properties derived from the `bookigFunnelSingleCompanyConfig` state,
 * such as visibility of credit card section, payment titles, quote titles, and mandatory flags.
 *
 * @returns {IUseBookingFunnelCSEConfig} An object containing computed properties for various configuration settings.
 */
const useBookingFunnelCSEConfig = (): IUseBookingFunnelCSEConfig => {
    /**
     * Indicates whether the credit card section should be hidden.
     * @type {ComputedRef<boolean>}
     */
    const isHideCreditCardSection: ComputedRef<boolean> = computed(() => {
        return !!bookigFunnelSingleCompanyConfig.value?.isHideCreditCardSection
    })
    const isHideInsuranceSection: ComputedRef<boolean> = computed(() => {
        return !!bookigFunnelSingleCompanyConfig.value?.isHideInsuranceSection
    })

    /**
     * The title displayed after a successful payment.
     * @type {ComputedRef<string>}
     */
    const successfulPaymentTitle: ComputedRef<string> = computed(() => {
        return bookigFunnelSingleCompanyConfig.value?.successfulPaymentTitle || ""
    })

    /**
     * The text displayed after a successful payment.
     * @type {ComputedRef<string>}
     */
    const successfulPaymentText: ComputedRef<string> = computed(() => {
        return bookigFunnelSingleCompanyConfig.value?.successfulPaymentText || ""
    })

    /**
     * The title displayed after a successful quote.
     * @type {ComputedRef<string>}
     */
    const successfulQuoteTitle: ComputedRef<string> = computed(() => {
        return bookigFunnelSingleCompanyConfig.value?.successfulQuoteTitle || ""
    })

    /**
     * The text displayed after a successful quote.
     * @type {ComputedRef<string>}
     */
    const successfulQuoteText: ComputedRef<string> = computed(() => {
        return bookigFunnelSingleCompanyConfig.value?.successfulQuoteText || ""
    })

    /**
     * Indicates whether participants are mandatory.
     * @type {ComputedRef<boolean>}
     */
    const isMandatoryParticipants: ComputedRef<boolean> = computed(() => {
        return !!bookigFunnelSingleCompanyConfig.value?.isMandatoryParticipants
    })

    /**
     * Indicates whether insurance is mandatory.
     * @type {ComputedRef<boolean>}
     */
    const isMandatoryInsurance: ComputedRef<boolean> = computed(() => {
        return !!bookigFunnelSingleCompanyConfig.value?.isMandatoryInsurance
    })

    /**
     * Indicates whether the SkiSchool is relevant.
     * @type {ComputedRef<boolean>}
     */
    const isSkiSchoolNL: ComputedRef<boolean> = computed(() => {
        return !!bookigFunnelSingleCompanyConfig.value?.isSkiSchoolNL
    })

    return {
        isHideCreditCardSection,
        isHideInsuranceSection,
        successfulPaymentTitle,
        successfulPaymentText,
        successfulQuoteTitle,
        successfulQuoteText,
        isMandatoryParticipants,
        isMandatoryInsurance,
        isSkiSchoolNL,
    }
}

export default useBookingFunnelCSEConfig